import React, { useState, useEffect } from "react";
import { auth } from "../../firebase";
import PopperPopup from "../PopperPopup";

export default function ContractCard({ contract, currentUser, onAccept }) {
  const [timeLeft, setTimeLeft] = useState("");
  const [loading, setLoading] = useState(false);
  const [isVoided, setIsVoided] = useState(false);

  const isOwner = contract.issuer === currentUser?.userDisplayName;

  useEffect(() => {
    const calculateTime = () => {
      const now = Date.now();
      const expiryTime = typeof contract.expiry === "number" ? contract.expiry : now;
      const difference = expiryTime - now;

      if (difference <= 0) {
        setTimeLeft("EXPIRED");
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((difference / 1000 / 60) % 60);

      let timeString = "";
      if (days > 0) timeString += `${days}D `;
      if (hours > 0 || days > 0) timeString += `${hours}H `;
      timeString += `${minutes}M`;
      setTimeLeft(timeString);
    };

    calculateTime();
    const timer = setInterval(calculateTime, 60000);
    return () => clearInterval(timer);
  }, [contract.expiry]);

  const executeVoid = async () => {
    setLoading(true);
    setIsVoided(true);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(
        "https://us-central1-stockmarket-83a73.cloudfunctions.net/cancelContract",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ contractId: contract.id }),
        }
      );
      if (!response.ok) throw new Error("Failed to void.");
    } catch (err) {
      setIsVoided(false);
      setLoading(false);
      alert(`Cancel failed: ${err.message}`);
    }
  };

  const VoidConfirmation = (
    <div style={{ padding: '15px', background: '#0a0a0a', border: '1px solid #ff4646', fontFamily: "'JetBrains Mono', monospace" }}>
      <div style={{ color: "#ff4646", fontWeight: 800, fontSize: '0.7rem', marginBottom: "8px" }}>
        TERMINATE CONTRACT
      </div>
      <p style={{ fontSize: "0.6rem", color: "#888", marginBottom: "12px" }}>
        Assets will be released from escrow immediately.
      </p>
      <button
        style={{ width: '100%', background: '#ff4646', color: '#000', border: 'none', padding: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.65rem' }}
        onClick={executeVoid}
        disabled={loading}
      >
        {loading ? "EXECUTING..." : "CONFIRM"}
      </button>
    </div>
  );

  if (isVoided) return null;

  // --- Inline Styles for Terminal Look ---
  const cardStyle = {
    background: "rgba(10, 15, 15, 0.9)",
    border: `1px solid ${contract.status === 'Accepted' ? 'rgba(80, 227, 194, 0.4)' : 'rgba(80, 227, 194, 0.15)'}`,
    padding: "0",
    fontFamily: "'JetBrains Mono', monospace",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column"
  };

  const labelStyle = { fontSize: "0.55rem", color: "#444", letterSpacing: "1px" };
  const valueStyle = { fontSize: "0.8rem", color: "#50e3c2" };

  return (
    <div style={cardStyle} className="contract-card-item">
      {/* Top Header Row */}
      <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", borderBottom: "1px solid rgba(80, 227, 194, 0.1)", background: "rgba(80, 227, 194, 0.03)" }}>
        <span style={{ fontSize: "0.6rem", fontWeight: "bold", color: "#50e3c2" }}>{contract.type.toUpperCase()}</span>
        <span style={{ 
          fontSize: "0.6rem", 
          color: timeLeft === "EXPIRED" ? "#ff4646" : "#50e3c2",
          opacity: contract.status === "Outstanding" ? 1 : 0.5
        }}>
          [{timeLeft === "EXPIRED" ? "EXPIRED" : contract.status.toUpperCase()}]
        </span>
      </div>

      {/* Main Data Area */}
      <div style={{ padding: "12px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
        {contract.type === "Loan" && (
          <>
            <div>
              <div style={labelStyle}>PRINCIPAL</div>
              <div style={{ ...valueStyle, fontWeight: "bold" }}>${contract.amount?.toLocaleString()}</div>
            </div>
            <div>
              <div style={labelStyle}>INTEREST</div>
              <div style={valueStyle}>{contract.interestRate}%</div>
            </div>
            <div style={{ gridColumn: "span 2", borderTop: "1px solid rgba(80, 227, 194, 0.05)", paddingTop: "8px" }}>
              <div style={labelStyle}>REPAYMENT TERM</div>
              <div style={valueStyle}>{contract.term?.replace("Hr", " HRS").replace("D", " DAYS")}</div>
            </div>
          </>
        )}

        {contract.type === "Exchange" && (
          <>
            <div>
              <div style={labelStyle}>OUTBOUND</div>
              <div style={valueStyle}>{contract.amount1} <span style={{fontSize: '0.6rem'}}>${contract.ticker1}</span></div>
            </div>
            <div>
              <div style={labelStyle}>INBOUND</div>
              <div style={{...valueStyle, color: "#F4C542"}}>{contract.amount2} <span style={{fontSize: '0.6rem'}}>${contract.ticker2}</span></div>
            </div>
            <div style={{ gridColumn: "span 2", borderTop: "1px solid rgba(80, 227, 194, 0.05)", paddingTop: "8px" }}>
              <div style={labelStyle}>TIME-TO-LIVE</div>
              <div style={{ ...valueStyle, color: timeLeft === "EXPIRED" ? "#ff4646" : "#50e3c2" }}>{timeLeft}</div>
            </div>
          </>
        )}

        {contract.type === "Option" && (
          <>
            <div>
              <div style={labelStyle}>UNDERLYING</div>
              <div style={valueStyle}>{contract.amount} ${contract.ticker}</div>
            </div>
            <div>
              <div style={labelStyle}>STRIKE PRICE</div>
              <div style={{...valueStyle, fontWeight: "bold"}}>${contract.strikePrice}</div>
            </div>
            <div style={{ gridColumn: "span 2", borderTop: "1px solid rgba(80, 227, 194, 0.05)", paddingTop: "8px" }}>
              <div style={labelStyle}>DURATION</div>
              <div style={valueStyle}>{timeLeft}</div>
            </div>
          </>
        )}
      </div>

      {/* Footer / Meta Data */}
      <div style={{ padding: "10px 12px", background: "rgba(0,0,0,0.2)", borderTop: "1px dotted rgba(80, 227, 194, 0.2)", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <div>
            <div style={labelStyle}>ISSUER</div>
            <div style={{ fontSize: "0.65rem", color: isOwner ? "#50e3c2" : "#888" }}>{contract.issuer.toUpperCase()}</div>
          </div>
          {contract.status === "Accepted" && (
            <div>
              <div style={labelStyle}>RECIPIENT</div>
              <div style={{ fontSize: "0.65rem", color: "#50e3c2" }}>{contract.recipient.toUpperCase()}</div>
            </div>
          )}
        </div>

        <div className="card-actions">
          {isOwner ? (
            contract.status === "Outstanding" ? (
              <PopperPopup popupContent={VoidConfirmation}>
                <button style={{ background: "transparent", border: "1px solid #ff4646", color: "#ff4646", padding: "4px 8px", fontSize: "0.6rem", cursor: "pointer", fontFamily: "'JetBrains Mono'" }}>
                  CANCEL
                </button>
              </PopperPopup>
            ) : (
              <span style={{ fontSize: "0.55rem", color: "#444" }}>[ ACTIVE ]</span>
            )
          ) : contract.status === "Accepted" ? (
            <span style={{ fontSize: "0.55rem", color: "#444" }}>[ LOCKED ]</span>
          ) : (
            <button 
              onClick={onAccept}
              style={{ background: "#50e3c2", color: "#000", border: "none", padding: "6px 12px", fontSize: "0.65rem", fontWeight: "bold", cursor: "pointer", fontFamily: "'JetBrains Mono'" }}
            >
              ACCEPT CONTRACT
            </button>
          )}
        </div>
      </div>
    </div>
  );
}